import { useMemo, useRef } from "react";
import { renderToString } from "react-dom/server";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getS13RankUrl } from "@outplayed/riot-assets";
import { calculateYAxisBounds, HighchartsLPDataPoint, generateMonthLabels } from "./helpers/year-in-review-lp-helpers";
import { getRankNameNoLp, getTierColor } from "@ugg/shared/utils/rank-helpers";
import miscSearch from "@ugitgud/legos/assets/src/shared/icon/svg/misc-search.svg";

const DEFAULT_STARTING_RANK = { tier: "UNRANKED", rank: "IV", lp: 0 };

interface YearInReviewLPGraphProps {
  series: HighchartsLPDataPoint[];
}

export function YearInReviewLPGraph(props: YearInReviewLPGraphProps) {
  const { series } = props;
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const startingRank = series[0]?.data ?? DEFAULT_STARTING_RANK;
  const monthLabels = generateMonthLabels();
  const { min: yMin, max: yMax } = useMemo(() => calculateYAxisBounds(series), [series]);

  const options: Highcharts.Options = {
    chart: {
      type: "spline",
      backgroundColor: "transparent",
      marginTop: 40,
      marginBottom: 40,
      marginLeft: 0,
      marginRight: 0,
      animation: false,
      zoomType: "x",
      panning: true,
      panKey: "shift",
      style: {
        cursor: "zoom-in",
      },
      resetZoomButton: {
        theme: {
          fill: "#3273FA",
          stroke: "none",
          style: {
            color: "#FFFFFF",
            fontWeight: "600",
          },
          r: 4,
          states: {
            hover: {
              fill: "#2563EB",
            },
          },
        },
        position: {
          align: "right",
          verticalAlign: "top",
          x: -10,
          y: 10,
        },
      },
    },

    // override defaults
    title: { text: "" },
    legend: { enabled: false },
    credits: { enabled: false },

    xAxis: {
      labels: {
        enabled: true,
        y: 24,
        style: {
          color: "#FFFFFF",
          fontSize: "12px",
          fontWeight: "600",
        },
        formatter: function () {
          const value = this.value as number;
          const positionIndex = monthLabels.tickPositions.findIndex((pos) => Math.abs(pos - value) < 0.001);
          if (positionIndex !== -1) {
            return monthLabels.categories[positionIndex] || "";
          }
          return "";
        },
      },
      tickPositions: monthLabels.tickPositions,
      min: 0,
      max: 1,
      lineColor: "transparent",
      tickLength: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      minPadding: 0,
      maxPadding: 0,
      startOnTick: false,
      endOnTick: false,
      crosshair: {
        width: 1,
        color: "rgba(50, 115, 250, 0.3)",
        snap: true,
      },
    },

    yAxis: {
      min: yMin,
      max: yMax,
      visible: false,
      minPadding: 0,
      maxPadding: 0,
    },

    series: [
      {
        type: "areaspline",
        data: series,
        color: "#3273FA",
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(50, 115, 250, 0.10)"],
            [1, "rgba(50, 115, 250, 0.00)"],
          ],
        },
        lineWidth: 2,
        zIndex: 2,
        findNearestPointBy: "x",
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 6,
              lineWidth: 2,
              lineColor: "#11112A",
              fillColor: "#3273FA",
            },
          },
        },
        states: {
          hover: {
            lineWidthPlus: 0,
            halo: {
              size: 0,
            },
          },
        },
        point: {
          events: {
            mouseOver: function () {
              const chart = this.series.chart;
              const xAxis = chart.xAxis[0];
              xAxis.removePlotLine("hover-line");

              // add long blue vertical plotLine at hovered point
              xAxis.addPlotLine({
                id: "hover-line",
                value: this.x,
                color: "#3273FA",
                width: 1,
                zIndex: 5,
              });
            },
            mouseOut: function () {
              const chart = this.series.chart;
              const xAxis = chart.xAxis[0];
              xAxis.removePlotLine("hover-line");
            },
          },
        },
      },
    ],

    tooltip: {
      backgroundColor: "rgba(0, 0, 0, 0)",
      borderColor: "transparent",
      borderWidth: 0,
      shadow: false,
      padding: 0,
      useHTML: true,
      positioner: function (this: Highcharts.Tooltip, labelWidth, labelHeight, point) {
        const chart = this.chart;
        const plotLeft = chart.plotLeft;
        const plotTop = chart.plotTop;
        const plotWidth = chart.plotWidth;

        // default: center tooltip on the tier image (15px from left edge)
        let x = point.plotX + plotLeft - 15;
        // switch tooltip side if tooltip overflows right edge
        if (x + labelWidth > plotLeft + plotWidth) x = point.plotX + plotLeft - labelWidth;

        return {
          x,
          y: plotTop - labelHeight - 10,
        };
      },
      formatter: function () {
        const point = this.point as unknown as HighchartsLPDataPoint;
        const { tier, rank, lp } = point.data;
        const abbreviatedRank = getRankNameNoLp(tier, rank, true);
        const tierColor = getTierColor(tier);
        const tierEmblem = getS13RankUrl(tier, true);

        return renderToString(
          <div className="flex items-center gap-2">
            <img
              src={tierEmblem}
              alt={tier}
              className="w-[30px] h-[30px] flex-shrink-0 aspect-square rounded-[2px] bg-[#11112A]"
            />
            <div className="flex items-center gap-1">
              <span className="font-bold text-[18px]" style={{ color: tierColor }}>
                {abbreviatedRank}
              </span>
              <span className="font-normal text-[14px] text-[#CDDCFE]">{lp} LP</span>
            </div>
          </div>,
        );
      },
    },
  };

  // callback to add utility to non-native functions to Highchart library
  const chartCallback = (chart: Highcharts.Chart): void => {
    if (chart) {
      chart.reflow();
      const renderer = chart.renderer;
      const defs = renderer.defs;
      const plotTop = chart.plotTop;
      const plotHeight = chart.plotHeight;
      const plotLeft = chart.plotLeft;
      const plotWidth = chart.plotWidth;

      // create blue linear gradient below the chart using absolute coordinates
      const gradient = renderer
        .createElement("linearGradient")
        .attr({
          id: "lp-gridline-gradient",
          x1: 0,
          y1: plotTop,
          x2: 0,
          y2: plotTop + plotHeight,
          gradientUnits: "userSpaceOnUse",
        })
        .add(defs);

      renderer
        .createElement("stop")
        .attr({
          offset: "0%",
          "stop-color": "#25254B",
          "stop-opacity": "1",
        })
        .add(gradient);

      renderer
        .createElement("stop")
        .attr({
          offset: "100%",
          "stop-color": "#25254B",
          "stop-opacity": "0",
        })
        .add(gradient);

      // create vertical y-axis label ticker gradients
      const xAxis = chart.xAxis[0];

      monthLabels.tickPositions.forEach((pos) => {
        const xPos = xAxis.toPixels(pos, false);

        renderer
          .rect(xPos, plotTop, 1, plotHeight)
          .attr({
            fill: "url(#lp-gridline-gradient)",
            "stroke-width": 0,
          })
          .add();
      });

      // create a dashed x-axis line
      const xAxisY = plotTop + plotHeight;
      const pattern = renderer
        .createElement("pattern")
        .attr({
          id: "axis-dash-pattern",
          patternUnits: "userSpaceOnUse",
          x: 0,
          y: 0,
          width: 8,
          height: 1,
        })
        .add(defs);

      renderer
        .rect(0, 0, 4, 1)
        .attr({
          fill: "#383864",
        })
        .add(pattern);

      renderer
        .rect(plotLeft, xAxisY, plotWidth, 1)
        .attr({
          fill: "url(#axis-dash-pattern)",
        })
        .add();
    }
  };

  return (
    // performing width calculation to extend past 12px width across the site
    <div className="relative rounded-lg -mx-3 w-[calc(100%+24px)]">
      <div className="absolute bottom-12 right-[18px] text-[#5C5C8E] text-[18px] z-10">
        Starting Rank:{" "}
        <span style={{ color: getTierColor(startingRank.tier) }}>
          {getRankNameNoLp(startingRank.tier, startingRank.rank, false)}
        </span>{" "}
        {startingRank.lp} LP
      </div>

      <div className="w-full year-in-review-lp-graph">
        <HighchartsReact
          highcharts={Highcharts}
          ref={chartRef}
          options={options}
          containerProps={{ style: { width: "100%", height: "448px" } }}
          callback={chartCallback}
        />
      </div>
    </div>
  );
}
